import { Plus, X } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
  DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { ApiError } from "@/lib/api";
import { sameValues } from "@/lib/form";
import {
  useCampaigns,
  useCreateWebhookSubscription,
  useUpdateWebhookSubscription,
  useWebhookEventTypes,
} from "@/lib/queries";
import type { WebhookEndpoint, WebhookSubscription } from "@/lib/types";
import {
  EVERY_CAMPAIGN,
  NEW_SUBSCRIPTION,
  eventTerm,
  eventTypeOrder,
  prettyJson,
  renderTemplate,
  samplePaths,
  subscriptionChanges,
  subscriptionPayload,
  subscriptionProblem,
  subscriptionValues,
  type ConditionRow,
  type SubscriptionValues,
} from "@/lib/webhooks";

/** Radix's Select cannot hold an empty value, so every campaign has a value of its own here. */
const ALL = "all";
const TEMPLATE_ID = "subscription-template";

/**
 * One event an endpoint hears, added or edited (ADR-022): which event, from which campaigns, only
 * when what, and in what body.
 *
 * The form is written against the event's sample, which core-api serves from the contract with the
 * `context` a real delivery carries beside it. A condition's path and a template's fields are picked
 * from it, and a template is previewed over it exactly as the webhooks service renders one.
 */
export function WebhookSubscriptionFormDialog({
  open,
  onOpenChange,
  endpoint,
  subscription,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  endpoint: WebhookEndpoint;
  /** Absent for create. */
  subscription?: WebhookSubscription;
}) {
  const isSlack = endpoint.kind === "slack";
  const defaults = isSlack
    ? { ...NEW_SUBSCRIPTION, body_mode: "message", post_in: "thread" as const }
    : NEW_SUBSCRIPTION;
  const toast = useToast();
  const create = useCreateWebhookSubscription();
  const update = useUpdateWebhookSubscription();
  const eventTypes = useWebhookEventTypes();
  const campaigns = useCampaigns({}, { enabled: open });
  const [values, setValues] = React.useState<SubscriptionValues>(NEW_SUBSCRIPTION);
  const [touched, setTouched] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setValues(subscription ? subscriptionValues(subscription) : defaults);
    setTouched(false);
    setFormError(null);
  }, [open, subscription, isSlack]);

  const set = <K extends keyof SubscriptionValues>(key: K, value: SubscriptionValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));
  const setCondition = (index: number, row: ConditionRow) =>
    set(
      "conditions",
      values.conditions.map((condition, i) => (i === index ? row : condition)),
    );

  const types = eventTypeOrder((eventTypes.data ?? []).map((t) => t.type));
  const sample = eventTypes.data?.find((t) => t.type === values.event_type)?.sample ?? null;
  const paths = samplePaths(sample);
  const tenantCampaigns = (campaigns.data ?? []).filter((c) => c.tenant_id === endpoint.tenant_id);
  const campaignGone =
    values.campaign_id !== EVERY_CAMPAIGN &&
    campaigns.data !== undefined &&
    !tenantCampaigns.some((c) => c.id === values.campaign_id);
  const chosen = values.event_type ? eventTerm(values.event_type) : null;
  const problem =
    subscriptionProblem(values) ??
    (isSlack &&
    values.body_mode === "message" &&
    !eventTypes.data?.find((t) => t.type === values.event_type)?.builtin_message
      ? "This event needs your own text; choose a template."
      : null);
  // Against what the effect above starts the form from, so opening it and changing nothing is no change.
  const changed = !sameValues(values, subscription ? subscriptionValues(subscription) : defaults);
  const template = values.body_mode === "template";
  const preview =
    sample && template ? renderTemplate(values.template, sample, isSlack ? "slack" : "json") : null;
  const previewProblem = !isSlack && preview !== null && preview.trim() !== "" && !parses(preview);

  /**
   * A field goes where the caret is. The textarea keeps its selection after the field's button takes
   * focus, so it is read from the element at the click, not tracked through key and mouse events,
   * which text dropped or pasted in does not raise.
   */
  const insertField = (path: string) => {
    const token = `{{${path}}}`;
    const area = document.getElementById(TEMPLATE_ID) as HTMLTextAreaElement | null;
    const start = area?.selectionStart ?? values.template.length;
    const end = area?.selectionEnd ?? values.template.length;
    set("template", values.template.slice(0, start) + token + values.template.slice(end));
    // Writing the new text puts the caret at the end; put it back after the field.
    window.setTimeout(() => {
      area?.focus();
      area?.setSelectionRange(start + token.length, start + token.length);
    }, 0);
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    setFormError(null);
    if (problem) return;
    try {
      const label = eventTerm(values.event_type).label;
      if (subscription) {
        const changes = subscriptionChanges(values, subscription);
        if (Object.keys(changes).length > 0) {
          await update.mutateAsync({
            endpointId: endpoint.id,
            id: subscription.id,
            ...changes,
          });
        }
        toast.success("Event saved", label);
      } else {
        await create.mutateAsync({
          endpointId: endpoint.id,
          ...subscriptionPayload(values),
        });
        toast.success("Event added", `${label} now goes to ${endpoint.name}`);
      }
      onOpenChange(false);
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the event.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer" size="lg">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{subscription ? "Edit event" : "Add an event"}</DialogTitle>
          <DialogDescription>
            What {endpoint.name} hears about, and the shape it arrives in.
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}
            {touched && problem && !formError && <Callout tone="danger">{problem}</Callout>}

            <Field
              label="Event"
              htmlFor="subscription-event"
              required
              hint={chosen?.description ?? "What happens on the platform that this endpoint hears about."}
            >
              <Select value={values.event_type} onValueChange={(next) => set("event_type", next)}>
                <SelectTrigger id="subscription-event" invalid={touched && !values.event_type}>
                  <SelectValue
                    placeholder={eventTypes.isPending ? "Reading the events…" : "Choose an event"}
                  />
                </SelectTrigger>
                <SelectContent>
                  {types.map((type) => {
                    const term = eventTerm(type);
                    return (
                      <SelectItem
                        key={type}
                        value={type}
                        description={term.sent ? type : `${type}, not sent yet`}
                      >
                        {term.label}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </Field>
            {chosen && !chosen.sent && (
              <Callout tone="warning">
                The platform does not send this event yet. The subscription is kept, and hears nothing until
                it does.
              </Callout>
            )}

            <Field
              label="From"
              htmlFor="subscription-campaign"
              hint="Every campaign's events, or one campaign's. An event that names no campaign reaches only a subscription to every campaign."
            >
              <Select
                value={values.campaign_id || ALL}
                onValueChange={(next) => set("campaign_id", next === ALL ? EVERY_CAMPAIGN : next)}
              >
                <SelectTrigger id="subscription-campaign">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL}>Every campaign</SelectItem>
                  {tenantCampaigns.map((campaign) => (
                    <SelectItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </SelectItem>
                  ))}
                  {campaignGone && (
                    <SelectItem value={values.campaign_id}>A campaign that is gone</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </Field>

            <div className="space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-medium text-text">Only when</p>
                  <p className="text-xs text-text-muted">
                    Every condition has to hold, or the event is not sent here. A path points into the event
                    as the sample below shows it: payload.disposition, or context.lead.language.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => set("conditions", [...values.conditions, { path: "", value: "" }])}
                >
                  <Plus />
                  Add a condition
                </Button>
              </div>
              {values.conditions.map((row, index) => (
                <div key={index} className="flex items-center gap-2">
                  <Input
                    aria-label={`Condition ${index + 1}, path`}
                    autoComplete="off"
                    className="min-w-0 flex-1 font-mono"
                    list="subscription-paths"
                    placeholder="payload.disposition"
                    value={row.path}
                    onChange={(e) => setCondition(index, { ...row, path: e.target.value })}
                  />
                  <Select
                    value={row.empty ? "empty" : "equals"}
                    onValueChange={(v) => setCondition(index, { ...row, empty: v === "empty" })}
                  >
                    <SelectTrigger className="w-28" aria-label={`Condition ${index + 1}, operator`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="equals">equals</SelectItem>
                      <SelectItem value="empty">is empty</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    aria-label={`Condition ${index + 1}, value`}
                    autoComplete="off"
                    className="w-40"
                    disabled={row.empty}
                    placeholder={row.empty ? "Missing or null" : "signed"}
                    value={row.value}
                    onChange={(e) => setCondition(index, { ...row, value: e.target.value })}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove condition ${index + 1}`}
                    onClick={() =>
                      set(
                        "conditions",
                        values.conditions.filter((_, i) => i !== index),
                      )
                    }
                  >
                    <X />
                  </Button>
                </div>
              ))}
              <datalist id="subscription-paths">
                {paths
                  .filter((p) => p.comparable)
                  .map((p) => (
                    <option key={p.path} value={p.path} />
                  ))}
              </datalist>
            </div>

            <Field
              label={isSlack ? "Message" : "Body"}
              htmlFor="subscription-body"
              hint={
                template
                  ? "A body of your own, for a receiver that expects a shape of its own, like a Slack message."
                  : isSlack
                    ? "A built-in call message, with rounded times and empty lines omitted."
                    : "The event as JSON, with who it is about beside it in context. What a Zapier catch hook wants."
              }
            >
              <Select value={values.body_mode} onValueChange={(next) => set("body_mode", next)}>
                <SelectTrigger id="subscription-body" className="w-56">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {isSlack ? (
                    <SelectItem value="message">Built-in message</SelectItem>
                  ) : (
                    <SelectItem value="event" description="The whole event, as JSON">
                      The event
                    </SelectItem>
                  )}
                  <SelectItem value="template" description="Your own body, with fields from the event">
                    A template
                  </SelectItem>
                </SelectContent>
              </Select>
            </Field>

            {isSlack && (
              <Field label="Post in" htmlFor="subscription-post">
                <Select
                  value={values.post_in ?? "thread"}
                  onValueChange={(v) => set("post_in", v as "thread" | "channel")}
                >
                  <SelectTrigger id="subscription-post">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="thread">The call's thread</SelectItem>
                    <SelectItem value="channel">The channel</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            )}
            {template && (
              <div className="grid gap-3 sm:grid-cols-[1fr_13rem]">
                <div className="space-y-1.5">
                  <Textarea
                    id={TEMPLATE_ID}
                    aria-label="Template"
                    className="min-h-40 font-mono text-xs"
                    placeholder={
                      isSlack
                        ? "*New call* for {{context.campaign.name}}"
                        : '{"text": "{{context.lead.first_name}}: {{payload.disposition}}"}'
                    }
                    value={values.template}
                    onChange={(e) => set("template", e.target.value)}
                  />
                  <p className="text-xs text-text-muted">
                    {isSlack ? (
                      "Plain text with {{path}} tokens. Values escape &, < and > for Slack; times are not rounded."
                    ) : (
                      <>
                        {" "}
                        {"{{path}}"} is replaced by the event's value there, escaped for JSON. Put a text
                        value inside quotes; a number, or true or false, may stand outside them. A path with
                        nothing there becomes nothing.
                      </>
                    )}
                  </p>
                </div>
                <div className="max-h-56 overflow-y-auto rounded-md border border-border">
                  <p className="sticky top-0 border-b border-border bg-surface px-2 py-1 text-2xs font-medium uppercase tracking-wide text-text-subtle">
                    Fields
                  </p>
                  {paths.length === 0 ? (
                    <p className="p-2 text-xs text-text-subtle">Choose an event to see its fields.</p>
                  ) : (
                    paths.map((p) => (
                      <button
                        key={p.path}
                        type="button"
                        title={p.example ? `In the sample: ${p.example}` : "Empty in the sample"}
                        className="block w-full truncate px-2 py-1 text-left font-mono text-xs text-text hover:bg-surface-hover"
                        onClick={() => insertField(p.path)}
                      >
                        {p.path}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            {sample && (
              <div className="space-y-1.5">
                <p className="text-xs font-medium text-text">
                  {template ? "What the template sends, over the sample" : "What the event looks like"}
                </p>
                {previewProblem && (
                  <Callout tone="warning">This is not valid JSON, and a receiver may refuse it.</Callout>
                )}
                <pre className="max-h-72 overflow-auto rounded-md border border-border bg-surface-sunken px-3 py-2 font-mono text-2xs text-text">
                  {template ? preview || "Nothing yet." : prettyJson(sample)}
                </pre>
              </div>
            )}

            {subscription && (
              <div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2.5">
                <div>
                  <p id="subscription-enabled-label" className="text-xs font-medium text-text">
                    Sends this event
                  </p>
                  <p className="text-xs text-text-muted">
                    Off, the endpoint stops hearing it and its log stays.
                  </p>
                </div>
                <Switch
                  checked={values.enabled}
                  onCheckedChange={(checked) => set("enabled", checked)}
                  aria-labelledby="subscription-enabled-label"
                />
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={create.isPending || update.isPending}>
              {subscription ? "Save changes" : "Add event"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}

function parses(text: string): boolean {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
}
